import { DateOnly, makeDateOnly } from 'types/date-only'

import dayjs from 'dayjs'

/**
 * The standard ISO representation of the end of time.
 */
export const MAX_DATE_ONLY_STRING = '9999-12-31'

/**
 * Converts a string or native Date object to a YYYY-MM-DD DateOnly string.
 * Returns an empty DateOnly for null, undefined, blank, or invalid input.
 */
export function toDateOnly(input: string | Date | null | undefined): DateOnly {
	if (input == null) return makeDateOnly('')

	if (input instanceof Date) {
		if (Number.isNaN(input.getTime())) return makeDateOnly('')

		return makeDateOnly(dayjs(input).format('YYYY-MM-DD'))
	}
	const trimmed = input.trim()
	if (!trimmed) return makeDateOnly('')

	if (trimmed.length >= 10 && trimmed[4] === '-' && trimmed[7] === '-') {
		return makeDateOnly(trimmed.slice(0, 10))
	}

	const parsed = new Date(trimmed)
	if (Number.isNaN(parsed.getTime())) return makeDateOnly('')

	return makeDateOnly(dayjs(parsed).format('YYYY-MM-DD'))
}

/**
 * Returns true if the given date represents the end of time (9999-12-31).
 */
export function isEndOfTime(date: DateOnly | Date | string | null | undefined): boolean {
	return toDateOnly(date).startsWith(MAX_DATE_ONLY_STRING)
}

/**
 * Formats a DateOnly string into a user-friendly display string.
 *
 * @param dateOnly - The branded ISO string to format.
 * @param format - The desired dayjs format (default: MM/DD/YYYY).
 * @returns The formatted date string.
 */
export function formatDateOnly(dateOnly: DateOnly, format: 'MM/DD/YYYY' = 'MM/DD/YYYY'): string {
	return dayjs(dateOnly).format(format)
}

/**
 * Splits a DateOnly string into its year, month, and day components as integers.
 *
 * Note: Assumes the input follows the YYYY-MM-DD pattern. If a time component is present,
 * it is effectively ignored during the day parsing because of how split and parseInt behave.
 *
 * @param dateOnly - The branded ISO string.
 * @returns An object containing the year, month, and day values.
 */
export function splitDateOnly(dateOnly: DateOnly) {
	const [year, month, day] = dateOnly.split('-')

	return {
		year: parseInt(year),
		month: parseInt(month),
		day: parseInt(day),
	}
}

export const formatAuditDate = (date: Date | string | undefined): string => {
	if (!date) return '—'

	return new Date(date).toLocaleString(undefined, {
		day: 'numeric',
		hour: 'numeric',
		minute: '2-digit',
		month: 'short',
		timeZoneName: 'short',
		year: 'numeric',
	})
}
